import { useCallback, useMemo, useState } from 'preact/hooks'
import { AccountChecklist } from './AccountChecklist'
import { AccountOnBoarding } from './AccountOnBoarding'
import { AccountOrders } from './AccountOrders'
import { useAccountLayoutContext } from './AccountProfileContext'
import { AccountYourFitClub } from './AccountYourFitClub'
import { Page } from './AccountPage'
import { useProfile } from '../hooks/useProfile'
import { RewardBlocks, useFitClubData } from './AccountFitClubLoyalty'
import { accountTranslations } from '../helpers/translations'

const generateChecklist = ({ profile, connections, handlePushState, handPushChildState }) => {
  return [
    {
      title: accountTranslations.dashboard.checklist.sign_up,
      complete: !!profile,
      callback: () => {}
    },
    {
      title: accountTranslations.dashboard.checklist.add_mobile,
      complete: !!profile.mobile_phone?.length,
      callback: () => {
        handlePushState('my-profile')
      }
    },
    {
      title: accountTranslations.dashboard.checklist.add_address,
      complete: !!profile?.address,
      callback: () => {
        handPushChildState('addresses', 'add_address')
      }
    },
    {
      title: accountTranslations.dashboard.checklist.add_children,
      complete: !!connections?.length,
      callback: () => {
        handlePushState('kids-profiles')
      }
    }
  ]
}

export const AccountDashboard = () => {
  const { cacheConfig, handlePushState, handPushChildState } = useAccountLayoutContext()
  const { getChildren } = useProfile()
  const { data: fitClubData, fetchData: getAccountFitClubData } = useFitClubData()

  const [accountDashboardData, setAccountDashboardData] = useState({
    profile: null,
    balance: null,
    hasChildren: false,
    checklist: [],
    card: {
      memberSince: null,
      code: null
    }
  })

  const accountDashboardDataFetch = useCallback(async () => {
    const profile = cacheConfig.cacheData.Profiles
    const balance = cacheConfig.cacheData.Balance

    const connections = await getChildren()
    const memberSince = new Date(profile?.created_at)
    const code = profile?.identities?.find((identity) => identity?.handle === 'ap21_card')

    const checklist = generateChecklist({ profile, connections, handlePushState, handPushChildState })
    const hasChildren = !!connections.length

    await getAccountFitClubData()
    setAccountDashboardData((c) => ({
      ...c,
      profile,
      balance,
      hasChildren,
      checklist,
      card: {
        memberSince: memberSince.getFullYear(),
        code: code?.identifier
      }
    }))
  }, [])

  const isFirstLogin = useMemo(() => localStorage.getItem('isFirstLogin') === 'true', [])

  return (
    <Page
      props={{
        page: {
          title: accountTranslations.dashboard.title
        },
        suspense: {
          asyncCallback: accountDashboardDataFetch,
          loadingState: (
            <div className="space-y-8 md:space-y-10 perspective-0">
              {isFirstLogin && <div className="h-[887px] animate-pulse bg-primary-grey md:h-[543px]" />}
              <div className="h-[475px] animate-pulse bg-primary-grey md:h-[301px]" />
              <div className="h-[340px] -rotate-1 animate-pulse bg-primary-grey md:h-[330px]" />
            </div>
          )
        }
      }}
    >
      {!accountDashboardData.hasChildren && <AccountOnBoarding />}
      {accountDashboardData?.card?.code && (
        <AccountYourFitClub
        card={{
          id: accountDashboardData?.card?.code,
          memberSince: accountDashboardData?.card?.memberSince,
          balance: accountDashboardData.balance?.reward_balance
        }}
        />
      )}
      <RewardBlocks fitClubData={fitClubData} />
      <AccountChecklist checklist={accountDashboardData.checklist} />
      <AccountOrders renderLimit={2} displayBanner={false} asSection={true} />
    </Page>
  )
}
